import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

import { api } from '../services/api';
import { fieldSectionSx } from './deviceConfigStyles';

export const EMPTY_REPORTS = {
  backhaul_clients_report: '',
  public_wifi_clients_report: '',
};

const ON_OFF_OPTIONS = [
  { value: '0', label: 'Off' },
  { value: '1', label: 'On' },
];

const valuesEqual = (a, b) =>
  a.backhaul_clients_report === b.backhaul_clients_report
  && a.public_wifi_clients_report === b.public_wifi_clients_report;

export function ReportsConfigFields({ cfg, onChange, disabled = false }) {
  return (
    <Stack spacing={2}>
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Backhaul clients report</InputLabel>
        <Select
          value={cfg.backhaul_clients_report || '0'}
          label="Backhaul clients report"
          onChange={(e) => onChange('backhaul_clients_report', e.target.value)}
        >
          {ON_OFF_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel>Public WiFi clients report</InputLabel>
        <Select
          value={cfg.public_wifi_clients_report || '0'}
          label="Public WiFi clients report"
          onChange={(e) => onChange('public_wifi_clients_report', e.target.value)}
        >
          {ON_OFF_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}

const ReportsSection = ({ serial, isJnet2 }) => {
  const [cfg, setCfg] = useState(EMPTY_REPORTS);
  const [original, setOriginal] = useState(EMPTY_REPORTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const fetchCfg = useCallback(async () => {
    if (!serial || !isJnet2) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getReportsConfig(serial);
      const r = data?.reports || {};
      const vals = {
        backhaul_clients_report: r.backhaul_clients_report || '0',
        public_wifi_clients_report: r.public_wifi_clients_report || '0',
      };
      setCfg(vals);
      setOriginal(vals);
    } catch (err) {
      setError(err?.message || 'Failed to load reports config');
    } finally {
      setLoading(false);
    }
  }, [serial, isJnet2]);

  useEffect(() => {
    fetchCfg();
  }, [fetchCfg]);

  const dirty = useMemo(() => !valuesEqual(cfg, original), [cfg, original]);

  const handleChange = (field, value) => {
    setCfg((prev) => ({ ...prev, [field]: value }));
    if (status) setStatus(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await api.setReportsConfig(serial, cfg);
      setOriginal(cfg);
      setStatus({ type: 'success', message: 'Reports configuration saved' });
    } catch (err) {
      setStatus({ type: 'error', message: err?.message || 'Failed to save reports configuration' });
    } finally {
      setSaving(false);
    }
  };

  if (!isJnet2) return null;

  return (
    <Paper variant="outlined" sx={fieldSectionSx}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            Client Reports
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Enable periodic association snapshots for backhaul and public WiFi clients.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchCfg}
            disabled={loading || saving}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!dirty || saving || loading}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {status && (
        <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
          {status.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <ReportsConfigFields cfg={cfg} onChange={handleChange} disabled={saving} />
      )}
    </Paper>
  );
};

export default ReportsSection;
